import { updateUrlWithSearchParamsForRange } from '@avensia/excite-esales';
import { styled } from '@glitz/react';
import { AdornedInput } from 'webapp/components/form/adorned-field';
import { FieldSize } from 'webapp/components/field-size';
import * as React from 'react';
import Slider from 'webapp/components/Slider';
import { useSeoSubpageData } from 'webapp/seo-subpages';
import { Heading } from './general-styling';
import { NnTestIdProps } from 'webapp/shared/test-ids';
import { useFormatPriceParts } from '@avensia/excite-ui';

let timer: number;

function debounce<Params extends any[]>(func: (...args: Params) => any, timeout: number): (...args: Params) => void {
  return (...args: Params) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout) as any;
  };
}

type PropType = {
  facet: Excite.Esales.FacetApiModel;
  onFacetValueChange: (updatedFacet: Excite.Esales.FacetApiModel, changedValue: Excite.Esales.RangeApiModel) => void;
};

function normalizeRange(range: Excite.Esales.RangeApiModel): Excite.Esales.RangeApiModel {
  return {
    minSelected: Math.floor(range.minSelected),
    min: Math.floor(range.min),
    maxSelected: Math.ceil(range.maxSelected),
    max: Math.ceil(range.max),
  };
}

export function RangeFacet({ facet, onFacetValueChange }: PropType) {
  const seoSubpageData = useSeoSubpageData();
  const prettyPrice = useFormatPriceParts();

  const [range, setRange] = React.useState(normalizeRange(facet.range));
  const values = React.useMemo(
    () => [Math.floor(facet.range.minSelected), Math.ceil(facet.range.maxSelected)],
    [facet.range],
  );
  const disabledRef = React.useRef(facet.range.max === facet.range.min);
  disabledRef.current = facet.range.max === facet.range.min;

  const [prefix, suffix] = React.useMemo(() => {
    let prefix,
      suffix = undefined;

    if (facet.attribute === 'price') {
      const p = prettyPrice(0);
      const fixIndex = p.findIndex(x => x.type === 'currency');
      if (fixIndex === 0) {
        prefix = p[fixIndex].value;
      } else if (fixIndex > 0) {
        suffix = p[fixIndex].value;
      }
    } else {
      const re = /\((\w+)\)/;
      const match = re.exec(facet.text);
      suffix = match?.at(1);
    }
    return [prefix, suffix];
  }, [facet.attribute, facet.text, prettyPrice]);

  const rangeMinActive = range.minSelected !== Math.floor(facet.range.min);
  const rangeMaxActive = range.maxSelected !== Math.ceil(facet.range.max);
  const rangeActive = rangeMinActive || rangeMaxActive;

  const isRangeValid = React.useCallback(
    (value: string | number, type: string): [boolean, number] => {
      if (typeof value === 'string') {
        value = parseInt(value);
      }
      if (isNaN(value)) {
        return [false, 0];
      }

      const curMin = Math.floor(facet.range.min);
      const curMax = Math.ceil(facet.range.max);

      switch (type) {
        case 'min':
          if (value < curMin) {
            return [false, curMin];
          } else if (value >= curMin && value <= facet.range.maxSelected) {
            return [true, value];
          } else if (value > facet.range.maxSelected) {
            return [false, facet.range.maxSelected];
          }
          break;
        case 'max':
          if (value > curMax) {
            return [false, curMax];
          } else if (value <= curMax && value >= facet.range.minSelected) {
            return [true, value];
          } else if (value < facet.range.minSelected) {
            return [false, facet.range.minSelected];
          }
          break;
      }
      return [false, 0];
    },
    [facet.range.minSelected, facet.range.min, facet.range.maxSelected, facet.range.max],
  );

  const updateRangeFacet = React.useCallback(
    (newRange: Excite.Esales.RangeApiModel) => {
      onFacetValueChange({ ...facet, range: newRange }, newRange);
      if (!seoSubpageData) {
        facet.range = newRange;
        updateUrlWithSearchParamsForRange(newRange, facet.attribute);
      }
    },
    [facet, onFacetValueChange, seoSubpageData],
  );

  const sliderDebounce = debounce((newRange: Excite.Esales.RangeApiModel) => {
    if (range.minSelected === newRange.minSelected && range.maxSelected === newRange.maxSelected) {
      return;
    }

    setRange(newRange);
    updateRangeFacet(newRange);
  }, 300);

  const handleInputChange = (minSelected: number, maxSelected: number): void => {
    const min = isRangeValid(minSelected, 'min');
    const max = isRangeValid(maxSelected, 'max');
    if (min[0] === false) {
      minSelected = min[1] > 0 ? min[1] : facet.range.minSelected;
    }
    if (max[0] === false) {
      maxSelected = max[1] > 0 ? max[1] : facet.range.maxSelected;
    }
    sliderDebounce(normalizeRange({ ...facet.range, ...{ minSelected, maxSelected } }));
  };

  React.useEffect(() => {
    setRange(normalizeRange(facet.range));
  }, [facet.range]);

  return (
    <Base key={`rangefacet${facet.attribute}`} aria-disabled={disabledRef.current} {...NnTestIdProps.RangeFacet}>
      <RangeContainer>
        <Heading>{facet.text}</Heading>
      </RangeContainer>
      <Slider
        onChange={handleInputChange}
        min={facet.range.min}
        max={facet.range.max}
        values={values}
        disabled={disabledRef}
      />
      <RangeInputFields css={rangeActive && rangeActiveDecorator}>
        <AdornedInput
          key={`rangefacet${facet.attribute}min`}
          type="number"
          inputmode="numeric"
          pattern="[0-9]*"
          css={rangeMinActive && rangeActiveDecorator}
          value={range.minSelected}
          invalid={!isRangeValid(range.minSelected, 'min')[0]}
          onValueChange={e => handleInputChange(parseInt(e), range.maxSelected)}
          prefix={prefix}
          suffix={suffix}
          disabled={disabledRef.current}
          size={FieldSize.XSmall}
        />
        <AdornedInput
          key={`rangefacet${facet.attribute}max`}
          type="number"
          inputmode="numeric"
          pattern="[0-9]*"
          css={rangeMaxActive && rangeActiveDecorator}
          value={range.maxSelected}
          invalid={!isRangeValid(range.maxSelected, 'max')[0]}
          onValueChange={e => handleInputChange(range.minSelected, parseInt(e))}
          prefix={prefix}
          suffix={suffix}
          disabled={disabledRef.current}
          size={FieldSize.XSmall}
        />
      </RangeInputFields>
    </Base>
  );
}

const RangeContainer = styled.div({
  padding: { right: t => t.sharedSpacing.l, bottom: t => t.sharedSpacing.s },
  display: 'flex',
  justifyContent: 'space-between',
});

const RangeInputFields = styled.div({
  display: 'inline-flex',
  position: 'relative',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '1em',
  margin: {
    top: '8px',
  },
});

const rangeActiveDecorator = styled({
  border: {
    xy: {
      color: t => t.facets?.range?.sliderMarkColor,
    },
  },
});

const Base = styled.li({
  padding: { xy: t => t.sharedSpacing.l },
  borderBottom: { style: 'solid', width: 'thin', color: t => t.facets?.borderColor },
});
